import { useCallback, useMemo } from "react";
import { cn } from "../lib/utils";
import { EditorTabs } from "./EditorTabs";
import { CodeEditor } from "./CodeEditor";
import type { EditorTab, TabbedCodeEditorProps } from "../types";

/**
 * TabbedCodeEditor — Composes EditorTabs + EditorToolbar + CodeEditor.
 *
 * All tab state is parent-controlled.
 * The active tab's content, language, and fileName are derived and
 * passed to the underlying CodeEditor.
 */
export function TabbedCodeEditor({
  // Tab state
  tabs,
  activeTabId,
  onTabChange,
  onTabClose,
  onTabDoubleClick,
  onTabReorder,
  onContentChange,

  // Editor config
  readOnly = false,
  theme = "vs-dark",
  diffViewMode = "inline",

  // Baked-in APIs
  formatApi,
  lintApi,

  // Exposed callbacks
  onSave,
  onSync,
  onOpenAIPanel,
  onDeploy,
  onLoadIntoChat,
  onMerge,
  onAbandon,
  onToggleViewMain,
  onToggleViewGithub,

  // External markers (e.g. static analysis)
  externalMarkers,

  // Coverage line decorations
  coverageLines,

  // State indicators (for active tab)
  isLoadingContent,
  isDirty,
  isSaving,
  isSyncing,
  isEdited,
  editedFilesCount,
  isDeploying,
  deploymentMessage,
  isAIPanelOpen,
  hasAnyActivityRunning,
  branchName,
  viewingMain,
  isMerging,
  isAbandoning,
  viewingGithub,
  githubContent,
  isLoadingGithub,

  // Monaco options
  editorOptions,

  // Sub-header slot
  subHeader,

  // Styling
  className,
}: TabbedCodeEditorProps) {
  // ── Derive active tab ──
  const activeTab = useMemo(
    () => tabs.find((t) => t.id === activeTabId) ?? null,
    [tabs, activeTabId],
  );

  // ── Tab handlers ──
  const handleTabClick = useCallback(
    (tab: EditorTab) => {
      onTabChange?.(tab.id);
    },
    [onTabChange],
  );

  const handleTabClose = useCallback(
    (tab: EditorTab) => {
      onTabClose?.(tab.id);
    },
    [onTabClose],
  );

  const handleTabDoubleClick = useCallback(
    (tab: EditorTab) => {
      onTabDoubleClick?.(tab.id);
    },
    [onTabDoubleClick],
  );

  // A branch exists once the file has been saved at least once (branchName is set).
  const hasBranch = !!branchName;

  // ── Editor content change → update active tab ──
  // Guard: skip when viewingMain, or viewingGithub AFTER a branch already
  // exists — Monaco fires onChange when the value prop is changed externally
  // (e.g. switching to a read-only preview), which would overwrite the user's
  // local edits in the store. Before a branch exists, viewingGithub means the
  // user is actively editing GitHub-origin content, so edits must propagate.
  const handleContentChange = useCallback(
    (value: string) => {
      if (activeTabId && !viewingMain && !(viewingGithub && hasBranch)) {
        onContentChange?.(activeTabId, value);
      }
    },
    [activeTabId, onContentChange, viewingGithub, viewingMain, hasBranch],
  );

  // ── Derive isDirty from active tab if not explicitly provided ──
  const resolvedIsDirty = isDirty ?? activeTab?.isDirty ?? false;

  const hasTabs = tabs.length > 0;

  return (
    <div className={cn("flex h-full min-h-0 min-w-0 flex-col bg-background", className)}>
      {/* Tab bar — shrink-0 + min-w-0 so tabs scroll horizontally instead of flex-crushing labels */}
      {hasTabs && (
        <div className="relative z-10 flex min-h-0 min-w-0 w-full shrink-0">
          <EditorTabs
            tabs={tabs}
            activeTabId={activeTabId}
            onTabClick={handleTabClick}
            onTabClose={onTabClose ? handleTabClose : undefined}
            onTabDoubleClick={onTabDoubleClick ? handleTabDoubleClick : undefined}
            onTabReorder={onTabReorder}
          />
        </div>
      )}

      {/* Sub-header slot — bundle nav strip, breadcrumbs, or any contextual row */}
      {subHeader && (
        <div className="shrink-0 min-w-0 border-b border-border">
          {subHeader}
        </div>
      )}

      {/* Editor area — flex-1 min-h-0 keeps Monaco/toolbar height stable when banners appear above */}
      {activeTab ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <CodeEditor
          value={viewingGithub && hasBranch && githubContent != null ? githubContent : activeTab.value}
          onChange={handleContentChange}
          language={activeTab.language ?? "plaintext"}
          fileName={activeTab.fileName ?? activeTab.label}
          readOnly={readOnly || (viewingGithub && hasBranch)}
          theme={theme}
          showToolbar={hasTabs}
          diff={activeTab.diff}
          diffViewMode={diffViewMode}
          formatApi={formatApi}
          lintApi={lintApi}
          onSave={onSave}
          onSync={onSync}
          onOpenAIPanel={onOpenAIPanel}
          onDeploy={onDeploy}
          onMerge={onMerge}
          onAbandon={onAbandon}
          onToggleViewMain={onToggleViewMain}
          onToggleViewGithub={onToggleViewGithub}
          isLoadingContent={isLoadingContent}
          isDirty={resolvedIsDirty}
          isSaving={isSaving}
          isSyncing={isSyncing}
          isEdited={isEdited}
          editedFilesCount={editedFilesCount}
          isDeploying={isDeploying}
          deploymentMessage={deploymentMessage}
          isAIPanelOpen={isAIPanelOpen}
          hasAnyActivityRunning={hasAnyActivityRunning}
          branchName={branchName}
          viewingMain={viewingMain}
          isMerging={isMerging}
          isAbandoning={isAbandoning}
          viewingGithub={viewingGithub}
          isLoadingGithub={isLoadingGithub}
          editorOptions={editorOptions}
          onLoadIntoChat={onLoadIntoChat}
          externalMarkers={externalMarkers}
          coverageLines={coverageLines}
          className="min-h-0 flex-1"
        />
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <p className="text-lg mb-2">No file open</p>
            <p className="text-sm">
              Select a file from the explorer to start editing
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
